import { CompoundUtils } from "./compound-utils";

import { console } from "#/log";
import { Compounds } from "#/declarations";

export class CompoundRender {
  /**
   * Common declaration of "empty" molecule
   **/
  public static readonly EMPTY: Compounds.Molecule = {
    moleculeId: "base",
    structure: "empty",
    options: {},
  } as const;

  /**
   * Adds the highlight flag to any bonds in `options.highlights`
   * @param molecule
   * @param options
   * @returns {Compounds.Structure}
   */
  public static addHighlightFlags(
    molecule: Compounds.Structure,
    options: Compounds.Options,
  ): Compounds.Structure {
    if (options && options.highlight && options.highlight.length > 0) {
      for (let j = 0; j < options.highlight.length; j++) {
        let bondArray = options.highlight[j];
        // h.length is 2 if it's a bond highlight (e.g. [["c1", "h1"]])
        if (bondArray.length === 2) {
          CompoundRender.addBondHighlight(molecule, bondArray);
          // h.length is 1 if it's an atom highlight (e.g. [["c1"]])
        } else if (bondArray.length === 1) {
          CompoundRender.addAtomHighlight(molecule, bondArray[0]);
        }
      }
    }
    return molecule;
  }

  /**
   * Gets the bounding rectangle of the molecule based on its
   * bond lines.
   */
  public static getBounds(molecule: Compounds.Structure, margin: number = 12) {
    let bounds = {
      left: Number.POSITIVE_INFINITY,
      right: Number.NEGATIVE_INFINITY,
      top: Number.POSITIVE_INFINITY,
      bottom: Number.NEGATIVE_INFINITY,
      width: 0,
      height: 0,
    };
    for (let i = 0; i < molecule.bonds.length; i++) {
      let bond = molecule.bonds[i];
      for (let j = 0; j < bond.lines.length; j++) {
        let line = bond.lines[j];
        if (line.x1 > bounds.right) {
          bounds.right = line.x1;
        }
        if (line.x1 < bounds.left) {
          bounds.left = line.x1;
        }
        if (line.y1 < bounds.top) {
          bounds.top = line.y1;
        }
        if (line.y1 > bounds.bottom) {
          bounds.bottom = line.y1;
        }
        if (line.x2 > bounds.right) {
          bounds.right = line.x2;
        }
        if (line.x2 < bounds.left) {
          bounds.left = line.x2;
        }
        if (line.y2 < bounds.top) {
          bounds.top = line.y2;
        }
        if (line.y2 > bounds.bottom) {
          bounds.bottom = line.y2;
        }
      }
    }
    bounds.left -= margin;
    bounds.right += margin;
    bounds.top -= margin;
    bounds.bottom += margin;
    bounds.width = bounds.right - bounds.left;
    bounds.height = bounds.bottom - bounds.top;
    return bounds;
  }

  /**
   * Performs steps 1 - 6 in the algorithm for generating molecule
   * positions, does not mutate original object.
   */
  public static layoutMolecule(
    molecule: Compounds.Structure,
    options: Compounds.Options,
  ): Compounds.Structure {
    console.debug("laying out molecule:", molecule);
    let mol = molecule;
    CompoundRender.addAtomLinkCount(mol);
    CompoundRender.addLinkAngles(mol);
    CompoundRender.addAtomAngles(mol);
    CompoundRender.addAtomPositions(mol, options.length || 20);
    CompoundRender.addBondPositions(mol, options.showHydrogen || true);
    CompoundRender.positionMolecule(mol);
    return mol;
  }

  /**
   * Generates line coordinates from a source to a target, with an
   * offset if there is one provided.
   * @returns {{x1: number, y1: number, x2: number, y2: number}}
   */
  public static getOffsetLine(
    molecule: Compounds.Structure,
    source: string,
    target: string,
    offset: number = 0,
  ) {
    let sourceAtom = CompoundUtils.getAtomById(molecule, source);
    let targetAtom = CompoundUtils.getAtomById(molecule, target);

    if (
      sourceAtom.x === undefined ||
      sourceAtom.y === undefined ||
      targetAtom.x === undefined ||
      targetAtom.y === undefined
    ) {
      throw new Error("Source or target atom position is undefined");
    }

    let x1 = sourceAtom.x;
    let x2 = targetAtom.x;
    let y1 = sourceAtom.y;
    let y2 = targetAtom.y;
    let line = Math.sqrt((x1 - x2) * (x1 - x2) + (y1 - y2) * (y1 - y2));
    return {
      x1: x1 + (offset * (y2 - y1)) / line,
      y1: y1 + (offset * (x1 - x2)) / line,
      x2: x2 + (offset * (y2 - y1)) / line,
      y2: y2 + (offset * (x1 - x2)) / line,
    };
  }

  /**
   * Average out all of the lines to get the average position.
   * @param bond
   * @returns {{x1: number, x2: number, y1: number, y2: number}}
   * @private
   */
  public static getLineAverage(bond: Compounds.Bond) {
    // average line for single / triple bonds is just the first line.
    if (
      bond.type === 1 ||
      bond.type === 3 ||
      (bond.source[0] === "c" && bond.target[0] === "c")
    ) {
      bond.avgLine = bond.lines[0];
      return bond.lines[0];
    }

    let avgLine = { x1: 0, x2: 0, y1: 0, y2: 0 };
    for (let i = 0; i < bond.lines.length; i++) {
      avgLine.x1 += bond.lines[i].x1;
      avgLine.x2 += bond.lines[i].x2;
      avgLine.y1 += bond.lines[i].y1;
      avgLine.y2 += bond.lines[i].y2;
    }
    avgLine.x1 /= bond.lines.length;
    avgLine.x2 /= bond.lines.length;
    avgLine.y1 /= bond.lines.length;
    avgLine.y2 /= bond.lines.length;
    bond.avgLine = avgLine;
    console.debug("compound-render: calculated average line", bond);
    return bond.avgLine;
  }

  /**
   * Adds the highlight (and label) flag to the provided atom id
   * @param molecule
   * @param atomId
   * @returns {Compounds.Structure}
   */
  public static addAtomHighlight(
    molecule: Compounds.Structure,
    atomId: string,
  ): Compounds.Structure {
    let atom = CompoundUtils.getAtomById(molecule, atomId);
    atom.highlight = true;
    atom.label = true;
    return molecule;
  }

  /**
   * Adds the highlight flags for a bond
   */
  public static addBondHighlight(
    molecule: Compounds.Structure,
    bondArray: Array<string>,
  ): Compounds.Structure {
    for (let i = 0; i < molecule.bonds.length; i++) {
      let bond = molecule.bonds[i];
      if (
        (bond.source === bondArray[0] || bond.source === bondArray[1]) &&
        (bond.target === bondArray[0] || bond.target === bondArray[1])
      ) {
        bond.highlight = true;
        this.addAtomHighlight(molecule, bond.source);
        this.addAtomHighlight(molecule, bond.target);
      }
    }
    return molecule;
  }

  /**
   * Gets the endpoint of a link with `length` and rotated by
   * angle from the origin (0,0)
   */
  private static getRotatedPoint(length: number, angle: number) {
    let adjustedAngle = 90 - angle;
    let radAngle = adjustedAngle * (Math.PI / 180);
    let newX = length * Math.cos(radAngle);
    let newY = length * Math.sin(radAngle) * -1; // invert Y for screen coords
    return { x: Math.round(newX * 100) / 100, y: Math.round(newY * 100) / 100 };
  }

  /**
   * Translates a molecule by the x and y values in the translate object
   */
  private static translateMolecule(
    molecule: Compounds.Structure,
    translate: { x: number; y: number },
  ): Compounds.Structure {
    for (let i = 0; i < molecule.atoms.length; i++) {
      const atom = molecule.atoms[i];

      if (typeof atom?.x === "number" && typeof atom?.y === "number") {
        atom.x += translate.x;
        atom.y += translate.y;
      }
    }

    for (let i = 0; i < molecule.bonds.length; i++) {
      let bond = molecule.bonds[i];
      for (let j = 0; j < bond.lines.length; j++) {
        bond.lines[j].x1 += translate.x;
        bond.lines[j].x2 += translate.x;
        bond.lines[j].y1 += translate.y;
        bond.lines[j].y2 += translate.y;
      }
    }
    return molecule;
  }

  /**
   * Add the number of connections to each of the atoms
   */
  private static addAtomLinkCount(
    molecule: Compounds.Structure,
  ): Compounds.Structure {
    for (let i = 0; i < molecule.atoms.length; i++) {
      molecule.atoms[i].links = CompoundUtils.getConnectionCount(
        molecule,
        molecule.atoms[i],
      );
    }
    return molecule;
  }

  /**
   * Add the angles for each of the links based on the
   * number of connections for the atom, and which link
   * number the bond is on the source atom.
   */
  private static addLinkAngles(
    molecule: Compounds.Structure,
  ): Compounds.Structure {
    for (let i = 0; i < molecule.bonds.length; i++) {
      let bond = molecule.bonds[i];
      let sourceAtom = CompoundUtils.getAtomById(molecule, bond.source);
      if (i === 0) {
        bond.angle = 0; // first link has no angle
        if (!sourceAtom.hasOwnProperty("angle")) {
          sourceAtom.angle = 0;
        }
      } else {
        if (typeof sourceAtom.links === "number" && sourceAtom.links > 0) {
          bond.angle =
            CompoundUtils.getLinkNumber(molecule, bond) *
            (360 / sourceAtom.links);
        } else {
          throw new Error(
            `sourceAtom.links is undefined or invalid for atom ${sourceAtom.id}`,
          );
        }
      }
    }
    return molecule;
  }

  /**
   * Adds the angle offset for each atom. The atom is rotated so it's
   * source node is at 0deg.
   */
  private static addAtomAngles(
    molecule: Compounds.Structure,
  ): Compounds.Structure {
    for (let i = 0; i < molecule.bonds.length; i++) {
      const bond = molecule.bonds[i];
      let sourceAtom = CompoundUtils.getAtomById(molecule, bond.source);
      let targetAtom = CompoundUtils.getAtomById(molecule, bond.target);

      if (
        !targetAtom.hasOwnProperty("angle") &&
        sourceAtom.angle !== undefined &&
        bond.angle !== undefined
      ) {
        targetAtom.angle = (sourceAtom.angle + bond.angle + 180) % 360;
      }
    }
    return molecule;
  }

  /**
   * Calculates the x and y positions of all atoms in the molecule
   * based on the source atom position, source atom angle, bond angle,
   * and the length of the bond.
   */
  private static addAtomPositions(
    molecule: Compounds.Structure,
    linkLength: number,
  ): Compounds.Structure {
    for (let i = 0; i < molecule.bonds.length; i++) {
      let bond = molecule.bonds[i];
      let sourceAtom = CompoundUtils.getAtomById(molecule, bond.source);
      let targetAtom = CompoundUtils.getAtomById(molecule, bond.target);
      if (!targetAtom.hasOwnProperty("x") && !targetAtom.hasOwnProperty("y")) {
        if (
          !sourceAtom.hasOwnProperty("x") &&
          !sourceAtom.hasOwnProperty("y")
        ) {
          // start at 0, 0
          sourceAtom.x = 0;
          sourceAtom.y = 0;
        }
        //Multiply bond length for C-C bonds by 2 to avoid overlap in chains
        let multiplier = bond.multiplier || 1;
        //This can be extended to resize other bonds.
        if (sourceAtom.name === "C" && targetAtom.name === "C") {
          multiplier = 2;
        }

        let relativePosition = this.getRotatedPoint(
          linkLength * multiplier,
          (bond.angle ?? 0) + (sourceAtom.angle ?? 0),
        );
        targetAtom.x = (sourceAtom.x ?? 0) + relativePosition.x;
        targetAtom.y = (sourceAtom.y ?? 0) + relativePosition.y;
      }
    }
    return molecule;
  }

  /**
   * Adds the lines attribute to the bonds in a molecule that have the x1,
   * x2, y1, y2 coordinates of the bond lines.  Creates multiple lines for
   * multiple bonds specified by `bond.type`.
   */
  private static addBondPositions(
    molecule: Compounds.Structure,
    showHydrogen: boolean,
  ): Compounds.Structure {
    for (let i = 0; i < molecule.bonds.length; i++) {
      let bond = molecule.bonds[i];
      let offsets = [0, -4, 4];
      if (
        bond.type === 2 &&
        !(bond.source[0] === "c" && bond.target[0] === "c")
      ) {
        // change offset for non-carbon double bonds
        offsets = [2, -2];
      }
      bond.lines = [];
      for (let i = 0; i < bond.type; i++) {
        // no links for hydrogen, unless showHydrogen is set
        if (
          (CompoundUtils.isHydrogen(molecule, bond.target) && showHydrogen) ||
          CompoundUtils.hasLabelFlag(bond.target, molecule) ||
          !CompoundUtils.isHydrogen(molecule, bond.target)
        ) {
          bond.lines.push(
            this.getOffsetLine(molecule, bond.source, bond.target, offsets[i]),
          );
        }
      }
    }
    return molecule;
  }

  /**
   * 6. Translates the molecule so it is centered in a container of `size`
   */
  private static positionMolecule(
    molecule: Compounds.Structure,
  ): Compounds.Structure {
    let bounds = this.getBounds(molecule);
    let targetPosition = {
      top: 0,
      left: 0,
    };
    let translateAmount = {
      x: targetPosition.left - bounds.left,
      y: targetPosition.top - bounds.top,
    };
    molecule.width = bounds.width;
    molecule.height = bounds.height;
    return this.translateMolecule(molecule, translateAmount);
  }
}
